import {HistoryStack} from 'components/customer/RoomPlanner/types';

export const SUPPORTED_ACTIONS = [
    'POSITION_CHANGE',
    'CABINET_ADD',
    'CABINET_DELETE',
    'CABINET_UPDATE',
];

export const isSupportedAction = (action: string): boolean => {
    return (
        action === 'POSITION_CHANGE' ||
        action === 'CABINET_ADD' ||
        action === 'CABINET_DELETE' ||
        action === 'CABINET_UPDATE'
    );
};

export const getEffectiveActiveIndex = (stack: HistoryStack[]): number => {
    if (!stack || stack.length === 0) return -1;

    // Prefer the last explicitly active item
    for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[Number(i)].isActive) {
            return i;
        }
    }

    // When nothing is active: treat index 0 as active only if first item is not POSITION_CHANGE
    // return stack[0]?.action !== 'POSITION_CHANGE' ? 0 : -1;
    return -1;
};

export const canUndo = (historyStack: HistoryStack[]) => {
    const currentActiveIndex = getEffectiveActiveIndex(historyStack);
    if (currentActiveIndex === -1) return false;

    const currentItem = historyStack[Number(currentActiveIndex)];

    // If the last active cabinet action (non–position) was just undone, disable undo
    if (currentActiveIndex === 0) {
        return false;
    }

    // Can undo if the current active entry itself is a supported action
    if (currentItem && isSupportedAction(currentItem.action)) {
        return true;
    }

    // Or if there's a previous supported action to go back to
    for (
        let targetIndex = currentActiveIndex - 1;
        targetIndex >= 0;
        targetIndex--
    ) {
        const candidateItem = historyStack[Number(targetIndex)];
        if (candidateItem && isSupportedAction(candidateItem.action)) {
            return true;
        }
    }

    return false;
};

export const canRedo = (historyStack: HistoryStack[]) => {
    const currentActiveIndex = getEffectiveActiveIndex(historyStack);
    if (currentActiveIndex === -1) return false;

    const currentItem = historyStack[Number(currentActiveIndex)];
    const isLastItem = currentActiveIndex + 1 === historyStack.length;

    // If the last active cabinet action (non–position) is recently redone, disable redo
    if (
        currentItem &&
        (currentItem.recentlyRedone === undefined ||
            currentItem.recentlyRedone === true) &&
        isLastItem
    ) {
        return false;
    }

    // Check if there's a next supported action entry
    for (
        let targetIndex = currentActiveIndex + 1;
        targetIndex < historyStack.length;
        targetIndex++
    ) {
        const candidateItem = historyStack[Number(targetIndex)];
        if (candidateItem && isSupportedAction(candidateItem.action)) {
            return true;
        }
    }

    // If the last item is a cabinet action (not POSITION_CHANGE), redo is possible
    const lastItem = historyStack[Number(historyStack.length - 1)];
    if (lastItem && isSupportedAction(lastItem.action)) {
        return true;
    }

    return false;
};

export const findLastActiveIndex = (
    stack: HistoryStack[]
): {index: number; isCabinetAction: boolean} => {
    for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[Number(i)].isActive) {
            return {
                index: i,
                isCabinetAction: SUPPORTED_ACTIONS.includes(
                    stack[Number(i)].action
                ),
            };
        }
    }
    return {index: -1, isCabinetAction: false};
};

export const findSupportedActionIndex = (
    stack: HistoryStack[],
    startIndex: number,
    direction: 'undo' | 'redo'
): number => {
    let i = startIndex;

    if (direction === 'undo') {
        while (i >= 0) {
            if (
                stack[Number(i)] &&
                SUPPORTED_ACTIONS.includes(stack[Number(i)].action)
            ) {
                return i;
            }
            i--;
        }
        return -1;
    }

    while (i < stack.length) {
        if (
            stack[Number(i)] &&
            SUPPORTED_ACTIONS.includes(stack[Number(i)].action)
        ) {
            return i;
        }
        i++;
    }
    return stack.length;
};
